import React, { useState, useEffect } from 'react';
import {
  getWhatsAppConfig,
  saveWhatsAppConfig,
  testWhatsAppConnection,
  type WhatsAppConfig,
} from '../../services/whatsappService';

interface WhatsAppSettingsProps {
  domain: string;
  onClose: () => void;
}

const WhatsAppSettings: React.FC<WhatsAppSettingsProps> = ({ domain, onClose }) => {
  const [config, setConfig] = useState<WhatsAppConfig>({
    enabled: false,
    accessToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    channelId: '',
    webhookVerifyToken: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [domain]);

  const loadConfig = async () => {
    setLoading(true);
    const savedConfig = await getWhatsAppConfig(domain);
    setConfig(savedConfig);
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const result = await saveWhatsAppConfig(domain, config, userData.name || 'Admin');

    if (result.success) {
      setTestResult({ success: true, message: 'Settings saved successfully!' });
    } else {
      setTestResult({ success: false, message: result.error || 'Failed to save settings' });
    }

    setSaving(false);
  };

  const handleTestConnection = async () => {
    if (!config.accessToken || !config.phoneNumberId) {
      setTestResult({ success: false, message: 'Please enter Access Token and Phone Number ID' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    const result = await testWhatsAppConnection(config.accessToken, config.phoneNumberId);

    if (result.success) {
      setTestResult({
        success: true,
        message: `Connection successful! Phone: ${result.phoneNumber}`,
      });
    } else {
      setTestResult({ success: false, message: result.error || 'Connection test failed' });
    }

    setTesting(false);
  };

  if (loading) {
    return (
      <div className="modal-body text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="bi bi-whatsapp me-2"></i>
          WhatsApp Channel Settings
        </h5>
        <button className="btn btn-light btn-sm" onClick={onClose}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div className="card-body">
        {/* Info Alert */}
        <div className="alert alert-info mb-4">
          <h6 className="alert-heading">
            <i className="bi bi-info-circle me-2"></i>
            How to Set Up WhatsApp Integration
          </h6>
          <ol className="mb-0 small">
            <li>Create a Meta Business Account at <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer">business.facebook.com</a></li>
            <li>Set up WhatsApp Business API in the Meta Developer Console</li>
            <li>Create a WhatsApp Channel for your school</li>
            <li>Get your Access Token and Phone Number ID from the API settings</li>
            <li>Enter the credentials below and test the connection</li>
          </ol>
        </div>

        {/* Enable Toggle */}
        <div className="form-check form-switch mb-4">
          <input
            className="form-check-input"
            type="checkbox"
            id="whatsappEnabled"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
          />
          <label className="form-check-label fw-bold" htmlFor="whatsappEnabled">
            Enable WhatsApp Integration
          </label>
        </div>

        <div className={config.enabled ? '' : 'opacity-50'}>
          {/* Access Token */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Access Token <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type={showToken ? 'text' : 'password'}
                className="form-control"
                placeholder="Enter your WhatsApp Business API Access Token"
                value={config.accessToken}
                onChange={(e) => setConfig({ ...config, accessToken: e.target.value })}
                disabled={!config.enabled}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowToken(!showToken)}
              >
                <i className={`bi ${showToken ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
            <small className="text-muted">
              Get this from Meta Developer Console → WhatsApp → API Setup
            </small>
          </div>

          {/* Phone Number ID */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Phone Number ID <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., 123456789012345"
              value={config.phoneNumberId}
              onChange={(e) => setConfig({ ...config, phoneNumberId: e.target.value })}
              disabled={!config.enabled}
            />
            <small className="text-muted">
              The unique ID of your WhatsApp Business phone number
            </small>
          </div>

          {/* Business Account ID */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Business Account ID
              <span className="text-muted fw-normal ms-2">(Optional)</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., 123456789012345"
              value={config.businessAccountId}
              onChange={(e) => setConfig({ ...config, businessAccountId: e.target.value })}
              disabled={!config.enabled}
            />
          </div>

          {/* Channel ID */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              WhatsApp Channel ID
              <span className="text-muted fw-normal ms-2">(For Channel Broadcasts)</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter your WhatsApp Channel ID"
              value={config.channelId}
              onChange={(e) => setConfig({ ...config, channelId: e.target.value })}
              disabled={!config.enabled}
            />
            <small className="text-muted">
              If you have a WhatsApp Channel, enter its ID to broadcast announcements
            </small>
          </div>

          {/* Webhook Verify Token */}
          <div className="mb-4">
            <label className="form-label fw-bold">
              Webhook Verify Token
              <span className="text-muted fw-normal ms-2">(Optional)</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Your custom webhook verification token"
              value={config.webhookVerifyToken}
              onChange={(e) => setConfig({ ...config, webhookVerifyToken: e.target.value })}
              disabled={!config.enabled}
            />
            <small className="text-muted">
              Used for webhook verification if you want to receive delivery reports
            </small>
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'} mb-4`}>
            <i className={`bi ${testResult.success ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
            {testResult.message}
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary"
            onClick={handleTestConnection}
            disabled={!config.enabled || testing || !config.accessToken || !config.phoneNumberId}
          >
            {testing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Testing...
              </>
            ) : (
              <>
                <i className="bi bi-plug me-2"></i>
                Test Connection
              </>
            )}
          </button>

          <button
            className="btn btn-success"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Save Settings
              </>
            )}
          </button>

          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>

      {/* Help Section */}
      <div className="card-footer bg-light">
        <h6 className="mb-2">
          <i className="bi bi-question-circle me-2"></i>
          Need Help?
        </h6>
        <div className="row small">
          <div className="col-md-6">
            <a
              href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
            >
              <i className="bi bi-book me-1"></i>
              WhatsApp Cloud API Documentation
            </a>
          </div>
          <div className="col-md-6">
            <a
              href="https://www.whatsapp.com/channel"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
            >
              <i className="bi bi-broadcast me-1"></i>
              Create WhatsApp Channel
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettings;
